"use client";

import { c as _c } from "react/compiler-runtime";
import { useContext } from "react";
import { AuthContext } from "@/components/auth/Auth";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { ApiDropType } from "@/generated/models/ApiDropType";
import { DropVoteState } from "./types";
import { Time } from "@/helpers/time";
interface DropInteractionRules {
  canShowVote: boolean; // determines if voting UI should be visible
  canVote: boolean; // determines if voting is enabled
  voteState: DropVoteState; // reason for current vote state
  canDelete: boolean; // determines if delete is allowed
  canSetPinnedDrop: boolean; // determines if wave pinned drop can be changed
  isAuthor: boolean; // determines if current user is the author
  isWinner: boolean; // determines if drop is a winner
  isVotingEnded: boolean; // determines if the voting period has ended for this drop's wave
  winningRank?: number | undefined; // rank of the winning drop if applicable
}

/**
 * Hook to determine various interaction rules for a drop
 * @param drop The drop to check rules for
 * @returns Object containing boolean flags for different interaction possibilities
 */
export function useDropInteractionRules(drop) {
  const $ = _c(29);
  const {
    connectedProfile,
    activeProfileProxy
  } = useContext(AuthContext);
  const isWinner = drop.drop_type === ApiDropType.Winner;
  const winningRank = isWinner ? drop.winning_context?.place : undefined;
  let t0;
  if ($[0] !== activeProfileProxy || $[1] !== connectedProfile || $[2] !== drop.context_profile_context?.max_rating || $[3] !== drop.drop_type || $[4] !== drop.id || $[5] !== drop.wave.authenticated_user_eligible_to_chat || $[6] !== drop.wave.authenticated_user_eligible_to_vote || $[7] !== drop.wave.voting_period_end || $[8] !== drop.wave.voting_period_start || $[9] !== isWinner) {
    const getVoteState = () => {
      if (!connectedProfile) {
        return DropVoteState.NOT_LOGGED_IN;
      }
      if (!connectedProfile.handle) {
        return DropVoteState.NO_PROFILE;
      }
      if (activeProfileProxy) {
        return DropVoteState.PROXY;
      }
      if (isWinner) {
        return DropVoteState.IS_WINNER;
      }
      const now = Time.currentMillis();
      if (drop.drop_type !== ApiDropType.Chat && drop.wave.voting_period_start && now < drop.wave.voting_period_start) {
        return DropVoteState.VOTING_NOT_STARTED;
      }
      if (drop.drop_type !== ApiDropType.Chat && drop.wave.voting_period_end && now > drop.wave.voting_period_end) {
        return DropVoteState.VOTING_ENDED;
      }
      if (drop.id.startsWith("temp-")) {
        return DropVoteState.CANT_VOTE;
      }
      if (drop.drop_type === ApiDropType.Participatory && !drop.wave.authenticated_user_eligible_to_vote || drop.drop_type === ApiDropType.Chat && !drop.wave.authenticated_user_eligible_to_chat) {
        return DropVoteState.CANT_VOTE;
      }
      if (!drop.context_profile_context?.max_rating) {
        return DropVoteState.NO_CREDIT;
      }
      return DropVoteState.CAN_VOTE;
    };
    t0 = getVoteState();
    $[0] = activeProfileProxy;
    $[1] = connectedProfile;
    $[2] = drop.context_profile_context?.max_rating;
    $[3] = drop.drop_type;
    $[4] = drop.id;
    $[5] = drop.wave.authenticated_user_eligible_to_chat;
    $[6] = drop.wave.authenticated_user_eligible_to_vote;
    $[7] = drop.wave.voting_period_end;
    $[8] = drop.wave.voting_period_start;
    $[9] = isWinner;
    $[10] = t0;
  } else {
    t0 = $[10];
  }
  const voteState = t0;
  let t1;
  if ($[11] !== activeProfileProxy || $[12] !== connectedProfile?.handle || $[13] !== drop.id) {
    t1 = !!connectedProfile?.handle && !activeProfileProxy && !drop.id.startsWith("temp-");
    $[11] = activeProfileProxy;
    $[12] = connectedProfile?.handle;
    $[13] = drop.id;
    $[14] = t1;
  } else {
    t1 = $[14];
  }
  const baseRules = t1;
  let t2;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [DropVoteState.NOT_LOGGED_IN, DropVoteState.NO_PROFILE, DropVoteState.PROXY, DropVoteState.CANT_VOTE, DropVoteState.IS_WINNER, DropVoteState.VOTING_NOT_STARTED, DropVoteState.VOTING_ENDED];
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  const canShowVote = !t2.includes(voteState);
  const canVote = voteState === DropVoteState.CAN_VOTE;
  const isAuthor = !!connectedProfile?.handle && connectedProfile.handle === drop.author.handle;
  const isAdmin = drop.wave.authenticated_user_admin;
  const adminDropDeletionEnabled = drop.wave.admin_drop_deletion_enabled;
  const canDeleteAsAdmin = isAdmin && adminDropDeletionEnabled;
  const canDelete = baseRules && (isAuthor || canDeleteAsAdmin);
  const canSetPinnedDrop = baseRules && isAdmin;
  const now_0 = Time.currentMillis();
  const isVotingEnded = drop.drop_type === ApiDropType.Participatory && drop.wave.voting_period_end ? now_0 > drop.wave.voting_period_end : false;
  let t3;
  if ($[16] !== isWinner || $[17] !== winningRank) {
    t3 = isWinner && winningRank ? {
      winningRank
    } : {};
    $[16] = isWinner;
    $[17] = winningRank;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  let t4;
  if ($[19] !== canDelete || $[20] !== canSetPinnedDrop || $[21] !== canShowVote || $[22] !== canVote || $[23] !== isAuthor || $[24] !== isVotingEnded || $[25] !== isWinner || $[26] !== t3 || $[27] !== voteState) {
    t4 = {
      canShowVote,
      canVote,
      voteState,
      canDelete,
      canSetPinnedDrop,
      isAuthor,
      isWinner,
      isVotingEnded,
      ...t3
    };
    $[19] = canDelete;
    $[20] = canSetPinnedDrop;
    $[21] = canShowVote;
    $[22] = canVote;
    $[23] = isAuthor;
    $[24] = isVotingEnded;
    $[25] = isWinner;
    $[26] = t3;
    $[27] = voteState;
    $[28] = t4;
  } else {
    t4 = $[28];
  }
  return t4;
}